import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { getPermissions } from '../api/configHelper';

const ITEMS_PER_PAGE = 10;

export default function Header({ onToggleSidebar }) {
  const [companyDetails, setCompanyDetails] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scrolled, setScrolled] = useState(false);
  const [userName, setUserName] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [emailId, setEmailId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);
  const [resetStep, setResetStep] = useState(1); // 1: enter details, 2: enter OTP
  const [otp, setOtp] = useState("");
  const [permissions, setPermissions] = useState({});
  const popupRef = useRef();
  const [taskCount, setTaskCount] = useState(0);
  const [taskList, setTaskList] = useState([]);
  const [showNotice, setShowNotice] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [taskLinks, setTaskLinks] = useState([]);
  const navigate = useNavigate();


  useEffect(() => {
  loadTasks();
}, []);

const loadTasks = async () => {
  try {
    const userName = sessionStorage.getItem("userName");
    const res = await api.GetUserTaskList(userName);

    setTaskList(res?.Table || []);
    setTaskCount(res?.Table1?.[0]?.TaskCount || 0);
    const links = res?.taskList?.Table2 ?? res?.Table2 ?? [];
    setTaskLinks(links);
  } catch (err) {
    console.error("Task load error", err);
  }
};

const getLinksByTask = (taskId) => {
  if (!taskId) return [];
  const normalizedId = String(taskId);
  return taskLinks.filter((link) => String(link.TaskId) === normalizedId);
};

  const totalPages = Math.ceil(taskList.length / ITEMS_PER_PAGE);
  const displayTotalPages = totalPages > 0 ? totalPages : 1;
  const paginatedTaskList = taskList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const paginationNeeded = totalPages > 1;

  useEffect(() => {
    if (currentPage > displayTotalPages) {
      setCurrentPage(displayTotalPages);
    }
  }, [currentPage, displayTotalPages]);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), displayTotalPages);
    if (nextPage === currentPage) return;
    setCurrentPage(nextPage);
  };
  useEffect(() => {
  const handleScroll = () => {
    setScrolled(window.scrollY > 10);
  };
  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
}, []);

    useEffect(() => {
  const details = sessionStorage.getItem("companyDetails");
  const name = sessionStorage.getItem("userName");

  if (details) {
    setCompanyDetails(JSON.parse(details));
  } else {
    navigate("/");
  }

  if (name) setUserName(name);
}, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsPopupOpen(false);
      }
    };
    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const perms = await getPermissions();
        setPermissions(perms);
      } catch (error) {
        console.error('Failed to load permissions:', error);
      }
    };
    loadPermissions();
  }, []);

const toggleTask = (taskId) => {
  setOpenTaskId(openTaskId === taskId ? null : taskId);
};

const handleMarkAsRead = async (taskId) => {
  const userName = sessionStorage.getItem("userName");

  try {
    // ✅ Step 1: Mark as read
    await api.MarkTaskAsRead({
      taskId: taskId,
      userName: userName,
      isRead: 1
    });

    // ✅ Step 2: Reload full data
    await loadTasks();

    // ✅ Step 3: Close popup
    setOpenTaskId(null);

  } catch (err) {
    console.error("Mark read error", err);
  }
};

const selectedTask = taskList.find((task) => task.TaskId === openTaskId) || null;

const handleLogout = async () => {
  const logId = sessionStorage.getItem("LogId");
  const userId = sessionStorage.getItem("userId");
  const currentAuthToken = sessionStorage.getItem("AuthToken");

  try {
    if (!logId || !userId) {
      console.error("Session values missing", { logId, userId,currentAuthToken });
      alert("Logout failed: session expired");
      return;
    }

    const result = await api.LogoutUser(logId, userId, currentAuthToken);

    // ✅ Works for both response shapes
    const message = result?.message || result?.data?.message;

    if (message) {
      alert(message);
    } else {
      alert("Logout successful");
    }
  } catch (error) {
    console.error("Logout error:", error);
    alert("Logout failed due to server error");
  } finally {
    sessionStorage.clear();
    localStorage.clear();
    navigate("/", { replace: true });
  }
};


  const handleResetPassword = async () => {
    if (resetStep === 1) {
      // Step 1: Request reset password
      if (!emailId || !newPassword) {
        setResetMessage({ text: "Please enter email and new password", type: "error" });
        return;
      }
      setResetLoading(true);
      try {
        const userName = sessionStorage.getItem("userName");
        const result = await api.RequestResetPassword({ userName, emailId, newPassword });
        setResetMessage({ text: result.message || "OTP sent to your email", type: "success" });
        setResetStep(2);
      } catch (err) {
        setResetMessage({ text: "Request failed: " + err.message, type: "error" });
      }
      setResetLoading(false);
    } else {
      // Step 2: Confirm with OTP
      if (!otp) {
        setResetMessage({ text: "Please enter the OTP", type: "error" });
        return;
      }
      setResetLoading(true);
      try {
        const userName = sessionStorage.getItem("userName");
        const result = await api.ConfirmResetPassword({ resetOtp: otp, userName });
        const message = typeof result === 'string' ? result : (result.message || "Password updated successfully");
        if (message.includes("Invalid or expired OTP")) {
          setResetMessage({ text: message, type: "error" });
        } else {
          setResetMessage({ text: message, type: "success" });
          setTimeout(() => {
            setIsResetModalOpen(false);
            setEmailId("");
            setNewPassword("");
            setOtp("");
            setResetMessage(null);
            setResetStep(1);
          }, 2000);
        }
      } catch (err) {
        setResetMessage({ text: "Confirmation failed: " + err.message, type: "error" });
      }
      setResetLoading(false);
    }
  };

  const formatDateTime = (date) => {
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

return (
  <>
    {/* ================= HEADER ================= */}
    <div
      className={`w-full fixed top-0 left-0 z-30 backdrop-blur-md shadow-md
      py-2 px-4 flex items-center justify-between rounded-lg transition-all duration-300
      ${scrolled ? "bg-base-100/40" : "bg-base-100/80"}`}
    >

    {/* LEFT SECTION — Toggle + Logo + Company Name */}
    <div className="flex items-center gap-3">
      {onToggleSidebar && (
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg bg-base-300 hover:bg-base-400 text-base-content"
        >
          ☰
        </button>
      )}
      <img
  src={companyDetails?.logoUrl}
  alt="Company Logo"
  className="object-cover rounded-lg shadow-sm w-13 h-9 sm:w-17 sm:h-11"
 />


      {companyDetails?.companyDetails?.Table?.[0] && (
        <h1 className="hidden text-lg font-bold tracking-wide sm:block md:text-xl text-base-content">
          {companyDetails.companyDetails.Table[0].ComName}
        </h1>
      )}
    </div>

    {/* CENTER SECTION — Empty for spacing */}
    <div className="flex-1"></div>
 <div className="relative flex items-center gap-3 mr-6">
  

  <div className="relative">
    <button
      onClick={() => setShowNotice(!showNotice)}
      className="relative flex items-center justify-center w-12 h-12 transition-all duration-300 rounded-full shadow-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      aria-label="Toggle noticeboard"
    >
      🔔

      {taskCount > 0 && (
        <span className="absolute -top-1 -right-1 px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full animate-pulse">
          {taskCount}
        </span>
      )}
    </button>

    {showNotice && (
      <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border shadow-2xl w-96 rounded-2xl border-base-200">

            <div className="flex items-center justify-between px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
              <h3 className="font-semibold">Noticeboard</h3>
              <button onClick={() => setShowNotice(false)}>✕</button>
            </div>

            <div className="p-3 space-y-3 overflow-y-auto max-h-80">
              {paginatedTaskList.map((task) => (
                <div
                  key={task.TaskId}
              className={`p-3 border rounded-lg cursor-pointer transition duration-200 ${
                task.IsRead
                  ? 'bg-green-50 border-green-300 hover:bg-green-100'
                  : 'bg-red-50 border-red-300 hover:bg-red-100'
              }`}
              onClick={() => toggleTask(task.TaskId)}
            >
              <div className="flex items-center justify-between text-sm font-semibold">
               <div>
  <div className={`font-semibold ${
    task.IsRead ? 'text-green-700' : 'text-red-700'
  }`}>
    {task.Title}
  </div>

  <div className="flex items-center justify-between w-full gap-12 mt-1 text-xs text-gray-500">
  
  {/* LEFT */}
  <span className="truncate max-w-[60%]">
    By - {task.UserName}
  </span>

  {/* RIGHT */}
  <span className="ml-auto text-right whitespace-nowrap">
    {new Date(task.Due_date).toLocaleDateString("en-GB")}
  </span>

</div>
</div>

                <div className="flex items-center gap-2">
                  {!task.IsRead && (
                    <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded">New</span>
                  )}

                  <span>{openTaskId === task.TaskId ? '▲' : '▼'}</span>
                </div>
              </div>

            </div>
          ))}
        </div>
        {paginationNeeded && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-xs font-semibold rounded-md transition bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
            >
              &lt;
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-2 py-1 text-xs font-semibold rounded-md transition ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-xs font-semibold rounded-md transition bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    )}
  </div>

<button
    onClick={() => setIsPopupOpen(!isPopupOpen)}
    className="flex items-center justify-center w-10 h-10 transition-colors rounded-full shadow-sm bg-primary text-primary-content hover:bg-primary-focus"
    title="User Menu"
  >
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  </button>
  
  <span
    className="hidden sm:block text-sm font-medium text-base-content truncate max-w-[140px]"
    title={userName}
  >
    {userName}
  </span>

  {isPopupOpen && (
    <div
      ref={popupRef}
      className="absolute right-0 z-40 p-2 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl top-full dark:bg-gray-800 dark:border-gray-600 min-w-80"
    >
      <div className="flex items-center gap-2 px-3 py-2 mb-2 text-sm text-gray-900 border-b border-gray-300 dark:text-gray-100 dark:border-gray-600">
        <svg className="w-4 h-4 text-gray-900 dark:text-gray-100" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        Login as {userName}
      </div>
      {permissions.resetPassword === 1 && (
        <button
          onClick={() => {
            setIsResetModalOpen(true);
            setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-blue-600 transition-colors rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:text-blue-400"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
          Change Password
        </button>
      )}
      {permissions.todoList === 1 && (
        <button
          onClick={() => {
            /* Todo list */ setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          Todo List
        </button>
      )}
      {permissions.activityLog === 1 && (
        <button
          onClick={() => {
            /* Activity Log */ setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          Activity Log
        </button>
      )}
      <button
        onClick={() => {
          handleLogout();
          setIsPopupOpen(false);
        }}
        className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
        Logout
      </button>
    </div>
  )}
</div>

    {/* RIGHT SECTION — Time + Logout */}
    <div className="flex items-center gap-4">
      {/* Time */}
      <span className="text-xs font-medium md:text-sm text-base-content/70">
        {formatDateTime(currentTime)}
      </span>



    </div>

    {/* Reset Password Modal */}
    {isResetModalOpen && (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-40 top-24 bg-black/40 backdrop-blur-md" onClick={() => setIsResetModalOpen(false)}></div>
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-start justify-center p-4 top-24" onClick={() => setIsResetModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 rounded-xl p-6 
                w-full max-w-xl md:max-w-xl lg:max-w-xl
                max-h-[70vh] overflow-y-auto shadow-xl">
               <h3 className="mb-4 text-xl font-semibold text-base-content">Change Password</h3>
               {resetStep === 1 ? (
                 <>
                 <div className="mb-4">
  <label className="block mb-1 text-sm font-medium text-base-content">
    Username
  </label>
  <input
    type="text"
    value={userName}
    disabled
    className="w-full px-3 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
  />
</div>
                   <div className="mb-4">
                     <label className="block mb-1 text-sm font-medium text-base-content">Email ID</label>
                     <input
                       type="email"
                       value={emailId}
                       onChange={(e) => setEmailId(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                       placeholder="Enter your email"
                     />
                   </div>
                   <div className="mb-4">
                     <label className="block mb-1 text-sm font-medium text-base-content">New Password</label>
                     <input
                       type="password"
                       value={newPassword}
                       onChange={(e) => setNewPassword(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                       placeholder="Enter new password"
                     />
                   </div>
                 </>
               ) : (
                 <div className="mb-4">
                   <label className="block mb-1 text-sm font-medium text-base-content">Enter OTP</label>
                   <input
                     type="text"
                     value={otp}
                     onChange={(e) => setOtp(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                     placeholder="Enter the OTP sent to your email"
                   />
                 </div>
               )}
            {resetMessage && (
              <div className={`mb-4 p-2 rounded-md text-sm ${resetMessage.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                {resetMessage.text}
              </div>
            )}
            <div className="flex justify-end gap-2">          
              <button
                onClick={handleResetPassword}
                disabled={resetLoading}
                className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-green-500 border border-green-600 rounded-md hover:bg-green-600 hover:border-green-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
              >
                {resetLoading ? (resetStep === 1 ? "Requesting..." : "Confirming...") : (resetStep === 1 ? "Request Change" : "Confirm Change")}
              </button>
                 <button
                onClick={() => {
                  setIsResetModalOpen(false);
                  setResetStep(1);
                  setEmailId("");
                  setNewPassword("");
                  setOtp("");
                  setResetMessage(null);
                }}
                className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-red-500 border border-red-600 rounded-md hover:bg-red-600 hover:border-red-700 hover:shadow-md hover:scale-105"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </>
    )}
  </div>

  {selectedTask && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-2xl animate-fadeIn">
        {/* 🔷 HEADER */}
        <div className="flex items-center justify-between px-5 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
          <h3 className="text-lg font-semibold tracking-wide">{selectedTask.Title}</h3>
          <button
            onClick={() => setOpenTaskId(null)}
            className="text-white transition hover:scale-110"
          >
            ✕
          </button>
        </div>

        {/* 🔹 BODY */}
        <div className="p-5 space-y-4">
          {/* Description */}
          <div className="p-3 border rounded-lg bg-gray-50">
            <div className="mb-1 text-sm font-semibold text-gray-600">Description</div>
            <div className="text-sm text-gray-800">{selectedTask.Description}</div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div className="p-2 border rounded-lg bg-blue-50">
              <div className="text-xs text-gray-500">Priority</div>
              <div className="font-semibold text-blue-700">{selectedTask.Priority}</div>
            </div>
            <div className="p-2 border rounded-lg bg-purple-50">
              <div className="text-xs text-gray-500">Status</div>
              <div className="font-semibold text-purple-700">{selectedTask.Status}</div>
            </div>
            <div className="p-2 border rounded-lg bg-green-50">
              <div className="text-xs text-gray-500">Start Date</div>
              <div className="font-semibold">
                {new Date(selectedTask.Start_date).toLocaleDateString("en-GB")}
              </div>
            </div>
            <div className="p-2 border rounded-lg bg-red-50">
              <div className="text-xs text-gray-500">Due Date</div>
              <div className="font-semibold text-red-600">
                {new Date(selectedTask.Due_date).toLocaleDateString("en-GB")}
              </div>
            </div>
          </div>

          {/* 🔹 ERP LINKS TABLE */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-700">Linked Documents</h4>
            <div className="overflow-hidden border rounded-lg">
              <div className="grid grid-cols-2 px-3 py-2 text-xs font-semibold text-white bg-gray-700">
                <div>Module</div>
                <div>Document</div>
              </div>
              <div className="overflow-y-auto text-sm max-h-40">
                {getLinksByTask(selectedTask.TaskId).map((link) => (
                  <div
                    key={link.LinkId}
                    className="grid grid-cols-2 px-2 py-2 transition border-b hover:bg-blue-50 gap-y-1"
                  >
                    <div>
                      <div className="font-medium text-blue-600">{link.Module}</div>
                      <div className="text-xs text-gray-500 truncate">{link.MdlId}</div>
                    </div>
                   <div className="text-gray-700 break-words whitespace-normal">
  {link.Document}
</div>
                  </div>
                ))}
                {getLinksByTask(selectedTask.TaskId).length === 0 && (
                  <div className="p-3 text-center text-gray-400">No linked documents</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 🔻 FOOTER */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          
       <button
  onClick={() => handleMarkAsRead(selectedTask.TaskId)}
  className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
>
  Read
</button>
        </div>
      </div>
    </div>
  )}
  </>
);
}
